import { ItemModel } from "../../../infrastructure/model/ProductItemModel";
import { SaleInfoResponse } from "../../../infrastructure/model/SaleInfoModel";
import { SALE_TYPE_ENUM, SALE_VALUE_TYPE_ENUM } from "../../constants/AppConst";

// ============= ENHANCED TYPES =============
export interface FlashDealItem extends ItemModel {
  saleInfo: {
    saleId: number;
    originalPrice: number;
    salePrice: number;
    discountPercent: number;
    discountAmount: number;
    saleEndDate: number;
    saleTitle: string;
    remaining?: number;
    totalStock?: number;
    // Thông tin đặc biệt cho CUP sale
    isCupSale?: boolean;
    cupBuyQuantity?: number; // Mua X cốc
    cupFreeQuantity?: number; // Tặng Y cốc
    cupDealText?: string; // Ví dụ: "Mua 4 tặng 1"
  };
}

// ============= UTILITY FUNCTIONS =============

/**
 * Kiểm tra xem sale có đang hoạt động không
 */
export function isSaleActive(sale: SaleInfoResponse): boolean {
  if (!sale.is_active) return false;

  const now = Date.now();

  // Kiểm tra sale theo thời gian cố định
  if (!sale.is_recurring) {
    return now >= sale.sale_start_date && now <= sale.sale_end_date;
  }

  // Kiểm tra sale lặp lại theo ngày trong tuần
  if (sale.is_recurring && sale.recurring_days?.length > 0) {
    const currentDay = new Date().getDay(); // 0=CN, 1=T2...
    const isRecurringDay = sale.recurring_days.includes(currentDay);

    // Vẫn phải nằm trong khoảng thời gian start/end
    const inDateRange = now >= sale.sale_start_date && now <= sale.sale_end_date;

    return isRecurringDay && inDateRange;
  }

  return false;
}

/**
 * Tính giá sau khi áp dụng sale
 */
export function calculateFlashDealPrice(
  originalPrice: number,
  sale: SaleInfoResponse
): {
  salePrice: number;
  discountAmount: number;
  discountPercent: number;
  isCupSale: boolean;
  cupBuyQuantity?: number;
  cupFreeQuantity?: number;
  cupDealText?: string;
} {
  let discountAmount = 0;
  let isCupSale = false;
  let cupBuyQuantity: number | undefined;
  let cupFreeQuantity: number | undefined;
  let cupDealText: string | undefined;

  if (sale.sale_value_type === SALE_VALUE_TYPE_ENUM.PERCENT) {
    // Giảm theo %
    discountAmount = (originalPrice * (sale.sale_value || 0)) / 100;

    // Áp dụng max_discount_value nếu có
    if (sale.max_discount_value && discountAmount > sale.max_discount_value) {
      discountAmount = sale.max_discount_value;
    }
  } else if (sale.sale_value_type === SALE_VALUE_TYPE_ENUM.VND) {
    // Giảm theo số tiền cố định
    discountAmount = sale.sale_value || 0;
  } else if (sale.sale_value_type === SALE_VALUE_TYPE_ENUM.CUP) {
    // Giảm theo cốc (mua X tặng Y)
    isCupSale = true;
    cupBuyQuantity = sale.sale_order_condition || 0; // Số cốc cần mua
    cupFreeQuantity = sale.sale_value || 0; // Số cốc tặng

    // Tính discount cho 1 cốc dựa trên deal
    // Ví dụ: Mua 4 tặng 1 = Mua 4 chỉ trả tiền 3
    // => Giảm 1/4 = 25% mỗi cốc khi mua đủ combo
    const totalCups = cupBuyQuantity;
    const paidCups = cupBuyQuantity - cupFreeQuantity;
    const discountPercentForDeal = (cupFreeQuantity / totalCups) * 100;

    discountAmount = (originalPrice * discountPercentForDeal) / 100;

    // Tạo text mô tả deal
    cupDealText = `Mua ${paidCups} tặng ${cupFreeQuantity}`;
  }

  const salePrice = Math.max(0, originalPrice - discountAmount);
  const discountPercent =
    originalPrice > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0;

  return {
    salePrice,
    discountAmount,
    discountPercent,
    isCupSale,
    cupBuyQuantity,
    cupFreeQuantity,
    cupDealText,
  };
}
/**
 * Lấy giá gốc của item (size nhỏ nhất)
 */
export function getItemBasePrice(item: ItemModel): number {
  if (!item.size_mapping || item.size_mapping.length === 0) return 0;

  // Sắp xếp theo size tăng dần và lấy giá của size nhỏ nhất
  const sortedSizes = [...item.size_mapping].sort((a, b) => a.size - b.size);
  return sortedSizes[0].price;
}

/**
 * Lấy danh sách items đang trong Flash Deal
 */
export function getFlashDealItems(
  allItems: ItemModel[],
  allSales: SaleInfoResponse[]
): FlashDealItem[] {
  // 1. Lọc các sale EACH_ITEM đang active
  const activeSales = allSales.filter(
    (sale) => sale.sale_type === SALE_TYPE_ENUM.EACH_ITEM && isSaleActive(sale)
  );

  if (activeSales.length === 0) return [];

  // 2. Tạo map để tra cứu nhanh sale theo item_id
  const itemSaleMap = new Map<number, SaleInfoResponse[]>();

  activeSales.forEach((sale) => {
    if (sale.apply_for && sale.apply_for.length > 0) {
      sale.apply_for.forEach((itemId) => {
        if (!itemSaleMap.has(itemId)) {
          itemSaleMap.set(itemId, []);
        }
        itemSaleMap.get(itemId)!.push(sale);
      });
    }
  });
  // 3. Tạo FlashDealItem cho các item có sale
  const flashDealItems: FlashDealItem[] = [];
  allItems.forEach((item) => {
    const itemSales = itemSaleMap.get(item.id);

    if (itemSales && itemSales.length > 0) {
      // Chọn sale có discount cao nhất (hoặc theo logic ưu tiên khác)
      const bestSale = selectBestSale(itemSales, item);
      const originalPrice = getItemBasePrice(item);
      const priceInfo = calculateFlashDealPrice(originalPrice, bestSale);

      // Tính remaining và totalStock (nếu có thông tin)
      const totalStock = bestSale.applicable_user_count || 0;
      const sold = bestSale.applied_user_count || 0;
      const remaining = totalStock > 0 ? totalStock - sold : undefined;

      flashDealItems.push({
        ...item,
        saleInfo: {
          saleId: bestSale.id,
          originalPrice,
          salePrice: priceInfo.salePrice,
          discountPercent: priceInfo.discountPercent,
          discountAmount: priceInfo.discountAmount,
          saleEndDate: bestSale.sale_end_date,
          saleTitle: bestSale.title,
          remaining,
          totalStock: totalStock > 0 ? totalStock : undefined,
          // Thông tin CUP sale
          isCupSale: priceInfo.isCupSale,
          cupBuyQuantity: priceInfo.cupBuyQuantity,
          cupFreeQuantity: priceInfo.cupFreeQuantity,
          cupDealText: priceInfo.cupDealText,
        },
      });
    }
  });
  // 4. Sắp xếp: ưu tiên CUP sale lên đầu, sau đó theo % giảm giá
  flashDealItems.sort((a, b) => {
    // Ưu tiên CUP sale
    if (a.saleInfo.isCupSale && !b.saleInfo.isCupSale) return -1;
    if (!a.saleInfo.isCupSale && b.saleInfo.isCupSale) return 1;

    // Cùng loại thì sắp xếp theo % giảm
    return b.saleInfo.discountPercent - a.saleInfo.discountPercent;
  });
  return flashDealItems;
}

/**
 * Chọn sale tốt nhất cho item (ưu tiên % giảm cao nhất)
 */
function selectBestSale(sales: SaleInfoResponse[], item: ItemModel): SaleInfoResponse {
  const originalPrice = getItemBasePrice(item);

  // Ưu tiên CUP sale
  const cupSale = sales.find((s) => s.sale_value_type === SALE_VALUE_TYPE_ENUM.CUP);
  if (cupSale) return cupSale;

  // Nếu không có CUP sale, chọn sale có % giảm cao nhất
  let bestSale = sales[0];
  let maxDiscount = 0;

  sales.forEach((sale) => {
    const { discountPercent } = calculateFlashDealPrice(originalPrice, sale);

    if (discountPercent > maxDiscount) {
      maxDiscount = discountPercent;
      bestSale = sale;
    }
  });

  return bestSale;
}

/**
 * Lấy thông tin Flash Deal tổng hợp (cho header section)
 */
export function getFlashDealSummary(flashDealItems: FlashDealItem[]): {
  totalStock: number;
  sold: number;
  remaining: number;
  percent: number;
  nearestEndTime: number | null;
  cupDealsCount: number;
  normalDealsCount: number;
} {
  let totalStock = 0;
  let sold = 0;
  let nearestEndTime: number | null = null;
  let cupDealsCount = 0;
  let normalDealsCount = 0;

  flashDealItems.forEach((item) => {
    // Đếm số lượng CUP deals
    if (item.saleInfo.isCupSale) {
      cupDealsCount++;
    } else {
      normalDealsCount++;
    }

    if (item.saleInfo.totalStock) {
      totalStock += item.saleInfo.totalStock;
      sold += item.saleInfo.totalStock - (item.saleInfo.remaining || 0);
    }

    // Tìm sale kết thúc sớm nhất
    if (!nearestEndTime || item.saleInfo.saleEndDate < nearestEndTime) {
      nearestEndTime = item.saleInfo.saleEndDate;
    }
  });

  const remaining = totalStock - sold;
  const percent = totalStock > 0 ? Math.round((sold / totalStock) * 100) : 0;

  return {
    totalStock,
    sold,
    remaining,
    percent,
    nearestEndTime,
    cupDealsCount,
    normalDealsCount,
  };
}

/**
 * Tính tổng tiền khi mua số lượng cụ thể với CUP sale
 */
export function calculateCupSaleTotal(
  quantity: number,
  pricePerCup: number,
  cupBuyQuantity: number,
  cupFreeQuantity: number
): {
  totalPrice: number;
  totalSaved: number;
  freeCupsReceived: number;
  effectivePricePerCup: number;
} {
  // Tính số combo hoàn chỉnh
  const fullCombos = Math.floor(quantity / cupBuyQuantity);
  const remainingCups = quantity % cupBuyQuantity;

  // Số cốc được miễn phí
  const freeCupsReceived = fullCombos * cupFreeQuantity;

  // Số cốc phải trả tiền
  const paidCups = fullCombos * (cupBuyQuantity - cupFreeQuantity) + remainingCups;

  // Tổng tiền phải trả
  const totalPrice = paidCups * pricePerCup;

  // Tổng tiền tiết kiệm
  const totalSaved = freeCupsReceived * pricePerCup;

  // Giá trung bình mỗi cốc
  const effectivePricePerCup = quantity > 0 ? totalPrice / quantity : pricePerCup;

  return {
    totalPrice,
    totalSaved,
    freeCupsReceived,
    effectivePricePerCup,
  };
}

/**
 * Lấy text mô tả deal cho CUP sale
 */
export function getCupSaleBadgeText(cupBuyQuantity: number, cupFreeQuantity: number): string {
  if (cupFreeQuantity === 1) {
    return `${cupBuyQuantity - 1}+1`;
  }
  return `${cupBuyQuantity - cupFreeQuantity}+${cupFreeQuantity}`;
}
